import React, { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { URL_CONST } from 'utils/urlConsts'

import { NAME_FOR_HISTORY_STORE } from 'containers/ResolutionCenter/constants'
import { ALL_TIMES } from 'containers/ResolutionCenter/IncidentFilters/constants'
import { QUICK_FILTER } from 'containers/ResolutionCenter/QuickFilters/constants'

import { Close, FillSparkle } from 'components/lib/DesignSystem/Icons'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  /* Figma node 4584:147555 — 4-stop blue gradient + primary/60 bottom border. */
  background: linear-gradient(
    87.75deg,
    #1929b4 6.29%,
    #5a8eff 27.22%,
    #3063fd 61.46%,
    #1929b4 101.17%
  );
  border-bottom: 2px solid rgba(48, 99, 253, 0.6);
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  color: var(--white);
  font-family: var(--t-typography-font-family-font-family-default), Poppins, sans-serif;

  @media (max-width: 850px) {
    display: none;
  }
`

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
  color: var(--white);
  opacity: 0.85;

  &:hover {
    opacity: 1;
  }
`

const Inner = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  max-width: 1200px;
  justify-content: center;
`

const Headline = styled.span`
  /* Figma: text = Poppins SemiBold 18/28; "NEW!" = Poppins Bold 20/28. */
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;

  strong {
    font-size: 20px;
    font-weight: 700;
    margin-right: 6px;
    line-height: 28px;
  }
`

// Figma CTA is a yellow ("warning") button; the shared DSv3 Button has no warning
// variant, so we override its colors here. The rounded, bordered shape is the inner
// `ButtonContainer` span — NOT the root <button> (which has no border-radius). Paint
// the fill + border on `button > span`; painting the root background instead left a
// square fill poking out past the rounded border at the corners.
const CtaSlot = styled.div`
  button > span {
    background-color: var(--t-colors-yellow-400, #ffca1b);
    border-color: var(--t-colors-yellow-400, #ffca1b);
  }

  button:hover > span {
    background-color: #f0bd00;
    border-color: #f0bd00;
  }

  button span,
  button p {
    color: var(--t-colors-yellow-950, #481b00);
  }
`

const AICreditsDiscoveryBanner = ({ onDismiss }) => {
  const history = useHistory()

  const handleTryItNow = useCallback(() => {
    // "Try it now" must NOT dismiss the banner (IC-49457) — only the X does.
    // The banner hides when that first investigation reaches a verdict, and
    // stays gone across logins via has_completed_plg_investigation.
    history?.push(`${URL_CONST.incidents.classified}?soc=1`, {
      [NAME_FOR_HISTORY_STORE]: {
        quickFilters: [QUICK_FILTER.MAIL],
        dateFilter: ALL_TIMES,
      },
    })
  }, [history])

  return (
    <Wrapper id="ai-credits-discovery-banner" role="region" aria-label="AI Credits discovery">
      <Inner>
        <FillSparkle fill="var(--white)" width="24px" height="24px" />
        <Headline id="ai-credits-discovery-banner-text">
          <strong>NEW!</strong> Try out your new AI SOC agent on any email incident
        </Headline>
        <CtaSlot>
          <Button
            id="ai-credits-discovery-banner-cta"
            size={SIZES.MEDIUM}
            variant={VARIANTS.SECONDARY}
            onClick={handleTryItNow}
          >
            Try it now
          </Button>
        </CtaSlot>
      </Inner>
      {onDismiss && (
        <CloseButton
          id="ai-credits-discovery-banner-close"
          type="button"
          aria-label="Dismiss banner"
          onClick={onDismiss}
        >
          <Close width="16px" height="16px" />
        </CloseButton>
      )}
    </Wrapper>
  )
}

export default AICreditsDiscoveryBanner
